import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface BottomSheetMobileProps {
    open: boolean;
    onClose: () => void;
    title?: string;
    children?: ReactNode;
    className?: string;
    /** Tampilkan input pencarian di bawah judul. Default: false. */
    searchable?: boolean;
    /** Nilai input pencarian (controlled). */
    searchValue?: string;
    /** Dipanggil setiap kali teks pencarian berubah. */
    onSearchChange?: (value: string) => void;
    /** Placeholder input pencarian. */
    searchPlaceholder?: string;
}


const BottomSheetMobile = ({
    open,
    onClose,
    title,
    children,
    className,
    searchable = false,
    searchValue = '',
    onSearchChange,
    searchPlaceholder = 'Cari...',
}: BottomSheetMobileProps) => {
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener('keydown', onKey);
        };
    }, [open, onClose]);

    return createPortal(
        <AnimatePresence>
            {open && (
                <div className="fixed inset-0 z-50 flex items-end justify-center">
                    <motion.button
                        type="button"
                        aria-label="Tutup"
                        onClick={onClose}
                        className="absolute inset-0 bg-black/40"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                    />
                    <motion.div
                        role="dialog"
                        aria-modal="true"
                        aria-label={title}
                        className={cn(
                            'relative flex max-h-[85vh] w-full max-w-full flex-col rounded-t-2xl bg-white pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_16px_rgba(0,0,0,0.12)] w-full',
                            className
                        )}
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', stiffness: 400, damping: 36 }}
                    >
                        <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-bg-grey-300" />
                        <div className="flex items-center justify-between px-6 py-3">
                            <p className="title2medium text-black">{title}</p>
                            <button type="button" onClick={onClose} aria-label="Tutup" className="-mr-2 p-2 text-black">
                                <X className="size-5" />
                            </button>
                        </div>
                        {searchable && (
                            <div className="mb-4 px-6">
                                <input
                                    type="text"
                                    className="input-db-text"
                                    placeholder={searchPlaceholder}
                                    value={searchValue}
                                    onChange={(e) => onSearchChange?.(e.target.value)}
                                    aria-label={searchPlaceholder}
                                />
                            </div>
                        )}
                        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">{children}</div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>,
        document.body
    );
};

export interface SheetOption {
    value: string;
    label: string;
    desc?: string;
}

export interface SheetOptionListProps {
    options: SheetOption[];
    value?: string;
    onSelect: (option: SheetOption) => void;
}

/** Daftar pilihan siap pakai untuk di dalam BottomSheetMobile. */
export const SheetOptionList = ({ options, value, onSelect }: SheetOptionListProps) => {
    return (
        <ul className="flex flex-col">
            {options.map((option) => {
                const selected = option.value === value;
                return (
                    <li key={option.value}>
                        <button
                            type="button"
                            onClick={() => onSelect(option)}
                            className={cn(
                                'flex w-full items-center justify-between gap-3 border-b border-bg-grey-300 py-3 text-left',
                                selected && 'text-text-blue-100'
                            )}
                        >
                            <span className="min-w-0">
                                <span className="title2 block truncate">{option.label}</span>
                                {option.desc && <span className="reg12 block text-text-grey">{option.desc}</span>}
                            </span>
                            {selected && <Check className="size-5 shrink-0" />}
                        </button>
                    </li>
                );
            })}
        </ul>
    );
};

export default BottomSheetMobile;
